import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Answer } from '@/components/common/Answer';
import { SkeletonLoader } from '@/components/common/StateViews';
import { formatSessionDate } from '@/lib/datetime';
import { ApiError } from '@/lib/api';
import { addCadeJudicialLink, fetchCadeJudicial, removeCadeJudicialLink } from '@/services/cade';
import type { CadeJudicialCase, CadeJudicialList, CadeSeiCourtDoc } from '@/services/types';

const SOURCES = ['ata', 'sei', 'client', 'court', 'other'] as const;

/**
 * The court cases an analyst has linked to this CADE process, each with what the court publishes.
 *
 * CADE's own record names no court case, so a link is a person's ENTRY: shown with its source and
 * the words that show it, never as a finding of this product. An absence of links is not an
 * absence of a lawsuit, and the panel says so. The court's side is read by number from the CNJ's
 * public index; when it cannot be read the link still shows, with the reason.
 */
export function JudicialPanel({ caseKey }: { caseKey: string }) {
  const { t } = useTranslation(['cade']);
  const queryClient = useQueryClient();
  const { data, isPending, error } = useQuery({
    queryKey: ['cade', 'judicial', caseKey],
    queryFn: () => fetchCadeJudicial(caseKey),
    retry: false,
  });
  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: ['cade', 'judicial', caseKey] });

  if (isPending) return <SkeletonLoader rows={2} />;
  // A failed read must not break the page it sits on.
  if (error) return null;

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4" data-judicial>
      <h2 className="text-sm font-semibold text-foreground">{t('cade:judicial.title')}</h2>
      <p className="text-xs text-muted-foreground" data-judicial-notice>
        {t('cade:judicial.notice')}
      </p>
      <Answer answer={data.judicial} scope="record" help={t('cade:judicial.help')}>
        {(list: CadeJudicialList) => (
          <ul className="space-y-3">
            {list.cases.map((item) => (
              <CaseCard key={item.link_id} item={item} onRemoved={refresh} />
            ))}
          </ul>
        )}
      </Answer>
      {data.sei_court && data.sei_court.length > 0 ? <SeiCourtDocs docs={data.sei_court} /> : null}
      {data.can_add ? <AddForm caseKey={caseKey} onAdded={refresh} /> : null}
    </section>
  );
}

/**
 * Court decisions CADE filed in the process's own SEI record. They show that a court decided
 * something in a related lawsuit; the document type says nothing of the outcome, and the court
 * case number is not read from them, so each stays a pointer to read.
 */
function SeiCourtDocs({ docs }: { docs: CadeSeiCourtDoc[] }) {
  const { t, i18n } = useTranslation(['cade']);
  return (
    <div className="space-y-1" data-judicial-sei>
      <h3 className="text-xs font-medium text-foreground">{t('cade:judicial.sei.title')}</h3>
      <p className="text-xs text-muted-foreground">{t('cade:judicial.sei.help')}</p>
      <ul className="space-y-1 text-sm">
        {docs.map((doc) => (
          <li key={doc.sei_doc_number} className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="tabular-nums text-foreground">
              {formatSessionDate(doc.doc_date, null, i18n.language) ?? '—'}
            </span>
            <span className="text-foreground">{doc.doc_type}</span>
            {doc.unit ? <span className="text-xs text-muted-foreground">{doc.unit}</span> : null}
            {doc.url ? (
              <a
                href={doc.url}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-primary underline underline-offset-4"
              >
                {t('cade:judicial.sei.open', { number: doc.sei_doc_number })}
              </a>
            ) : (
              <span className="text-xs text-muted-foreground">SEI {doc.sei_doc_number}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function CaseCard({ item, onRemoved }: { item: CadeJudicialCase; onRemoved: () => void }) {
  const { t, i18n } = useTranslation(['cade']);
  const remove = useMutation({
    mutationFn: () => removeCadeJudicialLink(item.link_id),
    onSuccess: onRemoved,
  });
  const date = (iso: string | null) => formatSessionDate(iso, null, i18n.language);
  return (
    <li className="space-y-2 rounded-md border p-3" data-judicial-case>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="font-medium tabular-nums text-foreground">{item.cnj_number}</span>
        <span className="text-xs text-muted-foreground">{item.court_label}</span>
        <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-foreground">
          {t(`cade:judicial.source.${item.source}`)}
        </span>
        <span className="text-xs text-muted-foreground">
          {t('cade:judicial.addedOn', { date: date(item.added_on) ?? item.added_on })}
        </span>
      </div>
      <blockquote className="text-xs italic text-foreground">“{item.evidence}”</blockquote>
      <CourtSide item={item} />
      {item.can_remove ? (
        <button
          type="button"
          disabled={remove.isPending}
          onClick={() => remove.mutate()}
          className="text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
        >
          {t('cade:judicial.remove')}
        </button>
      ) : null}
    </li>
  );
}

function CourtSide({ item }: { item: CadeJudicialCase }) {
  const { t, i18n } = useTranslation(['cade']);
  const record = item.record;
  if (item.lookup !== 'found' || !record) {
    return (
      <p className="text-xs text-muted-foreground" data-lookup={item.lookup}>
        {t(`cade:judicial.lookup.${item.lookup}`)}
      </p>
    );
  }
  const date = (iso: string | null) => formatSessionDate(iso, null, i18n.language);
  const facts: [string, string | null][] = [
    ['class', record.class_name],
    ['organ', record.organ],
    ['filed', date(record.filed)],
    ['subjects', record.subjects.length ? record.subjects.join('; ') : null],
  ];
  return (
    <div className="space-y-1" data-lookup="found">
      <p className="text-xs text-muted-foreground">{t('cade:judicial.lookup.found')}</p>
      <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
        {facts.map(([name, value]) =>
          value ? (
            <div key={name}>
              <dt className="text-muted-foreground">{t(`cade:judicial.field.${name}`)}</dt>
              <dd className="text-foreground">{value}</dd>
            </div>
          ) : null,
        )}
      </dl>
      {record.movements.length > 0 ? (
        <div>
          <h3 className="text-xs font-medium text-foreground">{t('cade:judicial.movements')}</h3>
          <ul className="text-xs text-foreground">
            {record.movements.map((m, index) => (
              <li key={`${m.when}-${index}`}>
                <span className="tabular-nums text-muted-foreground">{date(m.when) ?? '—'}</span>{' '}
                {m.name}
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground">{t('cade:judicial.noOutcome')}</p>
        </div>
      ) : null}
    </div>
  );
}

function AddForm({ caseKey, onAdded }: { caseKey: string; onAdded: () => void }) {
  const { t } = useTranslation(['cade']);
  const [cnj, setCnj] = useState('');
  const [source, setSource] = useState<(typeof SOURCES)[number]>('ata');
  const [evidence, setEvidence] = useState('');
  const add = useMutation({
    mutationFn: () => addCadeJudicialLink(caseKey, { cnj_number: cnj, source, evidence }),
    onSuccess: () => {
      setCnj('');
      setEvidence('');
      onAdded();
    },
  });
  const failure =
    add.error instanceof ApiError
      ? t(`cade:judicial.error.${add.error.code}`, { defaultValue: add.error.message })
      : null;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    add.mutate();
  };
  return (
    <form onSubmit={submit} className="space-y-2 border-t pt-3" data-judicial-form>
      <h3 className="text-xs font-semibold text-foreground">{t('cade:judicial.add')}</h3>
      <div className="flex flex-wrap gap-3">
        <label className="space-y-1 text-xs text-muted-foreground">
          {t('cade:judicial.cnj')}
          <input
            value={cnj}
            onChange={(event) => setCnj(event.target.value)}
            placeholder="0000000-00.0000.0.00.0000"
            className="block w-64 rounded-md border bg-background px-2 py-1 text-sm text-foreground"
          />
        </label>
        <label className="space-y-1 text-xs text-muted-foreground">
          {t('cade:judicial.sourceLabel')}
          <select
            value={source}
            onChange={(event) => setSource(event.target.value as (typeof SOURCES)[number])}
            className="block rounded-md border bg-background px-2 py-1 text-sm text-foreground"
          >
            {SOURCES.map((s) => (
              <option key={s} value={s}>
                {t(`cade:judicial.source.${s}`)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="block space-y-1 text-xs text-muted-foreground">
        {t('cade:judicial.evidence')}
        <textarea
          value={evidence}
          rows={2}
          maxLength={2000}
          onChange={(event) => setEvidence(event.target.value)}
          className="block w-full rounded-md border bg-background p-2 text-sm text-foreground"
        />
      </label>
      {failure ? (
        <p role="alert" className="text-xs text-destructive">
          {failure}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={add.isPending || cnj.trim().length < 15 || evidence.trim().length < 3}
        className="rounded-md bg-primary px-3 py-1 text-xs text-primary-foreground disabled:opacity-50"
      >
        {t('cade:judicial.addButton')}
      </button>
    </form>
  );
}
